// "Legs" section in CabinetEditor.
//
// One-click "Add 4 corner legs" for a floor cabinet (auto-disables the
// toe-kick part via partOverrides so the legs aren't hidden behind a
// kick board). Per-leg height/position editing isn't wired in the MVP
// — designers who need it can drop into JSON or hand-edit Cabinet.legs
// from the project document. The two knobs that DO survive: leg count
// (rebuilds from `defaultLegs(cabinet, height)`) and height.

import type { Cabinet } from "@/lib/types";
import {
  DEFAULT_LEG_HEIGHT,
  defaultLegs,
  legHardwareCount,
} from "@/lib/cabinets/legs";

interface Props {
  cabinet: Cabinet;
  onChange: (changes: Partial<Cabinet>) => void;
}

export function LegsSection({ cabinet, onChange }: Props) {
  // Wall-anchored cabinets don't get legs — they hang off the wall.
  if (cabinet.anchor === "wall") return null;

  const legs = cabinet.legs ?? [];
  const hasLegs = legs.length > 0;
  const height = legs[0]?.height ?? DEFAULT_LEG_HEIGHT;

  function addLegs(h = DEFAULT_LEG_HEIGHT) {
    // Legs DO NOT disable toe_kick — a real shop usually clips a
    // removable cosmetic kick board over the legs that aligns with
    // the cabinet's front edge. The toe_kick part stays where it is.
    onChange({ legs: defaultLegs(cabinet, h) });
  }

  function removeLegs() {
    onChange({ legs: undefined });
  }

  function setHeight(h: number) {
    if (!Number.isFinite(h) || h <= 0) return;
    // Rebuild legs at the new height — also bumps the TOE_KICK_H class
    // param so the carcass continues to sit flush on top of them.
    const newLegs = legs.map((l) => ({ ...l, height: h }));
    const classParams = { ...(cabinet.classParams ?? {}), TOE_KICK_H: h };
    onChange({ legs: newLegs, classParams });
  }

  return (
    <div style={{ marginTop: 16 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <div className="label">Legs</div>
        {hasLegs ? (
          <button
            className="ghost"
            style={{ fontSize: 11, padding: "2px 8px", color: "var(--danger)" }}
            onClick={removeLegs}
          >Remove</button>
        ) : (
          <button
            className="ghost"
            style={{ fontSize: 11, padding: "2px 8px" }}
            onClick={() => addLegs()}
            title="Add 4 corner legs (5 if cabinet is wider than 36″)"
          >+ Add</button>
        )}
      </div>

      {hasLegs ? (
        <div style={{ marginTop: 6, fontSize: 11, display: "grid", gridTemplateColumns: "auto 1fr", columnGap: 8, rowGap: 6, alignItems: "center" }}>
          <span className="muted">Count</span>
          <span>{legHardwareCount(cabinet)}</span>

          <span className="muted">Height ″</span>
          <input
            type="number" step={0.25} min={2} max={8}
            value={height}
            onChange={(e) => setHeight(Number(e.target.value))}
            style={{ width: "100%", fontSize: 11 }}
          />

          <span className="muted" style={{ alignSelf: "start" }}>Toe kick</span>
          <span style={{ color: "var(--fg-dim)" }}>
            stays — disable per-part if you want bare legs
          </span>
        </div>
      ) : (
        <div className="muted" style={{ fontSize: 10, marginTop: 4 }}>
          No legs. Click <strong>+ Add</strong> to place 4 corner feet (5 on cabinets ≥ 36″).
        </div>
      )}
    </div>
  );
}
